import React, { useEffect, useState } from 'react';
import {
    ConfirmAccept,
    VerificationCommentWrapper,
    VerificationIsssuesList,
    VerificationIssuesWrapper,
    VerificationList,
} from '../../pages/verification/Verification.styles';
import { DnvDataItem } from '../../types/ViewModel/Verification/DnvDataItem';
import { prettifyDateTimeHHmm } from 'odfjell-date-time-utils/dateTimeFormatter';
import { ReportMetadata } from '../../types/ViewModel/Verification/ReportMetadata';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import VerificationHeader from './VerificationHeader';
import { DNVReportStatus } from '../../enums/dnvReportStatusEnum';
import { compareDateBefore, compareVerificationIssues } from '../../utils/verification/verificationUtils';
import usePostUpdateReportComment from '../../api/usePostUpdateReportComment';
import { TextButton } from '../../styles/button.style';
import { useVerificationStore } from '../../context/VerificationContext';
import usePostUpdateVerificationData from '../../api/usePostUpdateVerificationData';
import { ReportMetadataBM } from '../../types/BusinessModel/Verification/ReportMetadataBM';
import { createReportMetadata } from '../../factory/Verification/ReportMetadataFactory';
import InternalVerificationIssues from './InternalVerificationIssues';

type Props = {
    selectedReportMetadata: ReportMetadata | null;
    isExpanded: boolean;
};

export enum VerificationViewEnum {
    List,
    Grid,
}

const DnvResponseListComponent: React.FC<Props> = (props) => {
    const [verificationIssuesList, setVerificationIssuesList] = useState<DnvDataItem[] | null>(null);
    const [filteredVerificationIssuesList, setFilteredVerificationIssuesList] = useState<DnvDataItem[] | null>(null);
    const [sortDesc, setSortDesc] = useState<boolean>(false);
    const [isShowDetails, setIsShowDetails] = useState<boolean>(false);
    const { upsertReportComment } = usePostUpdateReportComment();

    const {
        setSelectedReportMetadata,
        setPopulatedReportMetadataList,
        populatedReportMetadataList,
        reportComment,
        setReportComment,
        isCommentExpanded,
        showConfirmAccept,
        setShowConfirmAccept,
    } = useVerificationStore();
    const { acceptReport } = usePostUpdateVerificationData();

    useEffect(() => {
        if (verificationIssuesList && verificationIssuesList.length > 0) {
            sortDefault();
        } else {
            setFilteredVerificationIssuesList([]);
        }
    }, [verificationIssuesList]);

    useEffect(() => {
        if (props.selectedReportMetadata) {
            setSortDesc(false);
            setIsShowDetails(false);
            setReportComment({ reportId: props.selectedReportMetadata.reportId, comment: props.selectedReportMetadata.comment });
            setVerificationIssuesList(props.selectedReportMetadata.verificationList);
        }
    }, [props.selectedReportMetadata]);

    const sortDefault = () => {
        let _verificationIssuesList = verificationIssuesList;
        _verificationIssuesList = _verificationIssuesList?.sort((a, b) => compareDateBefore(a.eventDateTime, b.eventDateTime));
        _verificationIssuesList = _verificationIssuesList?.sort((a, b) => (a.severity > b.severity ? 1 : -1));
        setFilteredVerificationIssuesList([..._verificationIssuesList]);
    };

    const onChangeShowDetails = () => {
        if (isShowDetails === true) {
            setIsShowDetails(false);
            filteredVerificationIssuesList.forEach((f) => {
                f.showDetails = false;
            });
        } else {
            setIsShowDetails(true);
            filteredVerificationIssuesList.forEach((f) => {
                f.showDetails = true;
            });
        }
        setFilteredVerificationIssuesList([...filteredVerificationIssuesList]);
    };

    const onChangeSort = () => {
        if (sortDesc) {
            setSortDesc(false);
            let _verificationIssuesList = verificationIssuesList;
            _verificationIssuesList = _verificationIssuesList
                .sort((a, b) => (a.severity > b.severity ? 1 : -1))
                .sort((a, b) => compareVerificationIssues(a, b, sortDesc));
            setFilteredVerificationIssuesList([..._verificationIssuesList]);
        } else {
            setSortDesc(true);
            let _verificationIssuesList = verificationIssuesList;
            _verificationIssuesList = _verificationIssuesList
                .sort((a, b) => (a.severity > b.severity ? 1 : -1))
                .sort((a, b) => compareVerificationIssues(a, b, sortDesc));
            setFilteredVerificationIssuesList([..._verificationIssuesList]);
        }
    };

    const showListItemDetails = (index: number) => {
        const dnvItem = filteredVerificationIssuesList[index];
        if (dnvItem && dnvItem.showDetails === true) {
            dnvItem.showDetails = false;
        } else {
            dnvItem.showDetails = true;
        }
        setFilteredVerificationIssuesList([...filteredVerificationIssuesList]);
    };

    const updateCommentValue = (reportId: number, updatedComment: string) => {
        const _reportComment = { reportId: reportId, comment: updatedComment };
        setReportComment({ ..._reportComment });
    };

    const saveComment = async () => {
        const _reportComment = reportComment;
        if (_reportComment) {
            upsertReportComment(_reportComment);

            const _populatedReportMetadataList = populatedReportMetadataList;
            const _reportMetadataIndex = _populatedReportMetadataList.findIndex((r) => r.reportId === props.selectedReportMetadata.reportId);

            _populatedReportMetadataList[_reportMetadataIndex].comment = _reportComment.comment;
            setSelectedReportMetadata({ ..._populatedReportMetadataList[_reportMetadataIndex] });
        }
    };

    const cancelAccept = () => {
        setShowConfirmAccept(false);
    };

    const onAcceptReport = async () => {
        if (props.selectedReportMetadata) {
            let isToBeAccepted = true;
            if (props.selectedReportMetadata.acceptedAt != null && props.selectedReportMetadata.status == DNVReportStatus.VerifiedByDNV) {
                isToBeAccepted = false;
            }

            const _updateResponse = acceptReport(props.selectedReportMetadata.reportId, isToBeAccepted, reportComment);
            _updateResponse.then((result) => {
                if (result) {
                    const reportMetadataBM = result as ReportMetadataBM;
                    const _updatedReportMetadata = reportMetadataBM ? createReportMetadata(reportMetadataBM) : null;

                    const _populatedReportMetadataList = populatedReportMetadataList;
                    const _reportMetadataIndex = _populatedReportMetadataList.findIndex((r) => r.reportId === _updatedReportMetadata.reportId);
                    _populatedReportMetadataList[_reportMetadataIndex].status = _updatedReportMetadata.status;
                    _populatedReportMetadataList[_reportMetadataIndex].acceptedAt = _updatedReportMetadata.acceptedAt;
                    _populatedReportMetadataList[_reportMetadataIndex].acceptedByUser = _updatedReportMetadata.acceptedByUser;
                    _populatedReportMetadataList[_reportMetadataIndex].comment = _updatedReportMetadata.comment;

                    setPopulatedReportMetadataList([..._populatedReportMetadataList]);
                    setSelectedReportMetadata({ ..._populatedReportMetadataList[_reportMetadataIndex] });
                    setShowConfirmAccept(false);
                }
            });
        }
    };

    return (
        <VerificationList>
            <VerificationHeader
                report={props.selectedReportMetadata}
                isShowDetails={isShowDetails}
                onChangeShowDetails={onChangeShowDetails}
                sortDesc={sortDesc}
                onChangeSort={onChangeSort}
            ></VerificationHeader>
            {showConfirmAccept && (
                <ConfirmAccept>
                    {((!props.selectedReportMetadata.acceptedAt ||
                        (props.selectedReportMetadata.acceptedAt != null && props.selectedReportMetadata.status == DNVReportStatus.DNVIssues)) && (
                        <>
                            <h4>Confirm that you accept this report with</h4>
                            {props.selectedReportMetadata.numIssues > 0 && <h5>{props.selectedReportMetadata.numIssues} issues</h5>}
                            {props.selectedReportMetadata.numWarnings > 0 && <h5>{props.selectedReportMetadata.numWarnings} warnings</h5>}
                        </>
                    )) ||
                        (props.selectedReportMetadata.acceptedAt && <h4>Confirm that you want to undo accept</h4>)}
                    <TextButton className="inline" disabled={false} isSelected={true} onClick={() => onAcceptReport()}>
                        Confirm
                    </TextButton>
                    <TextButton className="inline" disabled={false} onClick={cancelAccept}>
                        Cancel
                    </TextButton>
                </ConfirmAccept>
            )}
            {isCommentExpanded && (
                <VerificationCommentWrapper>
                    <textarea
                        onChange={(e) => updateCommentValue(props.selectedReportMetadata.reportId, e.target.value)}
                        value={reportComment && reportComment.comment ? reportComment.comment : ''}
                    />

                    <TextButton onClick={() => saveComment()}>Update comment</TextButton>
                </VerificationCommentWrapper>
            )}

            <VerificationIssuesWrapper className={isCommentExpanded ? 'comment-expanded' : ''}>
                {props.selectedReportMetadata.internalVerificationIssues && props.selectedReportMetadata.internalVerificationIssues.length > 0 && (
                    <InternalVerificationIssues selectedReportMetadata={props.selectedReportMetadata}></InternalVerificationIssues>
                )}

                {props.selectedReportMetadata && filteredVerificationIssuesList && filteredVerificationIssuesList.length > 0 && (
                    <>
                        {props.selectedReportMetadata.status == DNVReportStatus.NewVersionAvailable && (
                            <span className="new-version-warning">
                                <FontAwesomeIcon className="notice-icon" icon="exclamation-triangle" />A new version of the report is available and
                                ready to be sent to DNV. The warnings/issues below have been generated based on the previous version. Please come back
                                24h after the report has been sent to verify if these warnings/issues are still actual.
                            </span>
                        )}
                        {props.selectedReportMetadata.hasOldVerificationIssues && (
                            <span className="old-issues-warning">
                                <FontAwesomeIcon className="notice-icon" icon="exclamation-triangle" />A new report has been sent to DNV after these
                                warnings/issues have been received. Please come back 24h after the report has been sent to verify if these
                                warnings/issues are still actual.
                            </span>
                        )}
                        <VerificationIsssuesList
                            className={props.selectedReportMetadata.status === DNVReportStatus.NewVersionAvailable ? 'new-version' : ''}
                        >
                            {filteredVerificationIssuesList.map((v, i) => {
                                return (
                                    <li key={v.imo + i}>
                                        <div className="header-section" onClick={() => showListItemDetails(i)}>
                                            {!props.isExpanded && (
                                                <>
                                                    <div className="row issues">
                                                        <span>{v.checkName}</span>

                                                        <span className={v.severity === 'Issue' ? 'issue' : 'warning'}>{v.severity}</span>
                                                    </div>
                                                    <div className="row heading">
                                                        <span>{prettifyDateTimeHHmm(v?.eventDateTime)}</span>
                                                        <span>{v.eevent}</span>
                                                    </div>
                                                    {!v.eevent && v?.portFromCode && (
                                                        <div className="row row-2">
                                                            <span>{prettifyDateTimeHHmm(v?.legStart)}</span>
                                                            <span>{v.portFromCode}</span>
                                                            <span>-</span>
                                                            <span>{prettifyDateTimeHHmm(v?.legEnd)}</span>
                                                            <span>{v.portToCode}</span>
                                                        </div>
                                                    )}
                                                    <div className="row">
                                                        <div className="col-2">
                                                            <span className="label">Verification data set</span> {v.schemaName}
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                            {props.isExpanded && (
                                                <>
                                                    <div className="row heading expanded">
                                                        <div className="row issues">
                                                            <span>{v.checkName}</span>
                                                        </div>
                                                        <div className="row heading">
                                                            <span>{prettifyDateTimeHHmm(v?.eventDateTime)}</span>
                                                            <span>{v.eevent}</span>
                                                        </div>
                                                        {!v.eevent && v?.portFromCode && (
                                                            <div className="row row-2">
                                                                <span>{prettifyDateTimeHHmm(v?.legStart)}</span>
                                                                <span>{v.portFromCode}</span>
                                                                <span>-</span>
                                                                <span>{prettifyDateTimeHHmm(v?.legEnd)}</span>
                                                                <span>{v.portToCode}</span>
                                                            </div>
                                                        )}
                                                        <span className={v.severity === 'Issue' ? 'issue' : 'warning'}>{v.severity}</span>
                                                    </div>
                                                    {v?.portFromCode && (
                                                        <div className="row row-2">
                                                            <div className="col-2">
                                                                <span className="label">Verification data set</span> {v.schemaName}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="row"></div>
                                                </>
                                            )}
                                        </div>
                                        {v.showDetails && (
                                            <div className="desc-section">
                                                <div>
                                                    <div className="row row-desc">
                                                        <span className="label">Details</span>
                                                    </div>
                                                    <div className="row">{v.details}</div>
                                                </div>
                                                {v.recommendation && (
                                                    <div>
                                                        <div className="row row-desc">
                                                            <span className="label">Recommendation</span>
                                                        </div>
                                                        <div className="row">{v.recommendation}</div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </VerificationIsssuesList>
                    </>
                )}
            </VerificationIssuesWrapper>
        </VerificationList>
    );
};
export default DnvResponseListComponent;
